import { Image, LayoutGrid, Menu } from 'lucide-react';
import * as React from 'react';
import { useHistory } from 'react-router';
import { useRecoilValue } from 'recoil';
import { AVisibleAdditionalColumns } from '../../../atoms/additionalColumns';
import { AVisibleAdditionalFieldColumns, fieldTypeToFilterType } from '../../../atoms/additionalFieldColumns';
import { AVisibleCalculatedFields } from '../../../atoms/calculatedFields';
import { AFormFields, AFormTemplate, Field } from '../../../atoms/forms';
import { DbView, FormView } from '../../../atoms/global/views';
import AdvancedFilters from '../../../components_v2/filter/AdvancedFilters';
import { filterTreeLength } from '../../../components_v2/filter/model/Model';
import { Filter, FilterParameter, FilterParameterParent } from '../../../components_v2/filter/pages/FilterList';
import ToolbarFilterButton from '../../../components_v2/toolbarFilter/ToolbarButton';
import { getUrlParameterByName } from '../../../components_v2/utils';
import { ToolBarView } from '../../../components_v2/view/ToolBarView';
import { translateToString } from '../../../styles/global/translate';
import { checkUrlModalParams } from '../../client-companies/Companies';
import { generateFormFilters } from '../../client-companies/CompaniesAndMapView';
import { getCompanyFilters } from '../../client-companies/data/action';
import { FilterState } from '../../contact/Contacts';
import { TableContext, TableProvider } from '../../contact/data/ContactContext';
import GalleryModalLeft from '../../gallery_v2/GalleryModalLeft';
import { ModalLeftToggle } from '../../map/modalLeft/ModalLeftFilter';
import { ToolbarBox } from '../../globals/defaultToolbar/style/Style';
import { ComponentProps, ToolbarState } from '../../globals/mainPage/mainPage';
import { IconsGroupBackground, SwapImageContainer } from '../../map/PageSwitch';
import NoData from '../../noData/NoData';
import { ModalState } from '../../products/model';
import { getFormInstancesFilters } from '../actions';
import { InstancePopup } from '../formInstancePopup';
import { FormTemplate } from '../jsonValidator';
import FormInstanceGallery from './FormInstanceGallery';
import FormInstanceKanban from './FormInstanceKanban';
import FormInstanceList from './FormInstanceList';
import styled from 'styled-components';

export type FormInstanceViewSubProps = ComponentProps & {
	template: FormTemplate | undefined,
	formId: number,
	fieldMap: { [key: number | string]: Field },
	fieldColumns: { [key: number]: Field },
	setFilterState: React.Dispatch<React.SetStateAction<FilterState>>,
	viewLoaded: boolean,
	openPopup: (uuid: string) => void
};

const FORM_VIEW_LOCAL_STORAGE = 'form_view_id';
export const FORM_HIDDEN_LOCAL_STORAGE = 'form_hidden_columns';
export const FORM_SORT_LOCAL_STORAGE = 'form_sort_columns';
const FORM_ID_LOCAL_STORAGE = 'form_id';

const GalleryRightPartWrapper = styled.div<{ isModalOpen: boolean, isGallery: boolean }>`
  margin-left: ${p => (p.isModalOpen ? 265 : 0) + (p.isGallery ? 20 : 0)}px;
  transition: 0.25s;
`;

export default function FormInstanceView(props: ComponentProps) {
	return <TableProvider>
		<_FormInstanceView {...props} />
	</TableProvider>;
}

type ViewEnum = 'list' | 'kanban' | 'gallery';

function matchHash(hash: string) {
	switch (hash) {
		case '#kanban':
			return 'kanban';
		case '#gallery':
			return 'gallery';
		default:
			return 'list';
	}
}

function isValidHash(hash: string) {
	return hash === '#list' || hash === '#kanban' || hash === '#gallery';
}

function _FormInstanceView(props: ComponentProps) {
	const [subComponentToolBarState, setSubComponentToolBarState] = React.useState<ToolbarState>();
	const [instanceModal, setInstanceModal] = React.useState<ModalState<string>>({ isOpen: false });
	const history = useHistory();
	const forms = useRecoilValue(AFormTemplate).instanceForms;
	if (forms.length === 0) {
		return <NoData message={translateToString('no_forms_contact_sidely')} />;
	}
	const [view, setView_] = React.useState<ViewEnum>(matchHash(history.location.hash));
	let formId = parseInt(getUrlParameterByName('id') ?? 'NaN');
	if (!forms || isNaN(formId)) {
		const localStorageId = parseInt(localStorage.getItem(FORM_ID_LOCAL_STORAGE) ?? 'NaN');
		formId = isNaN(localStorageId) ? forms[0].id : localStorageId;
		localStorage.setItem(FORM_ID_LOCAL_STORAGE, formId.toString());
		history.replace({ search: `?id=${formId}`, hash: view });
	} else {
		localStorage.setItem(FORM_ID_LOCAL_STORAGE, formId.toString());
	}
	const setView = React.useCallback((view: ViewEnum) => {
		setView_(view);
		return history.replace({ search: `?id=${formId}`, hash: view });
	}, [formId]);
	const template = React.useMemo(() => forms.find(a => a.id === formId), [formId, forms]);
	const additionalColumns = useRecoilValue(AVisibleAdditionalColumns);
	const additionalFieldColumns = useRecoilValue(AVisibleAdditionalFieldColumns);
	const calculatedFields = useRecoilValue(AVisibleCalculatedFields);


	const context = React.useContext(TableContext);
	const { hiddenColumns, sortColumns, filterResult, setFilterResult, setSortColumns, setHiddenColumns } = context;

	// FILTERS
	const [baseFilterParams, setBaseFilterParams] = React.useState<FilterParameterParent[]>([]);
	const [filterParams, setFilterParams] = React.useState<FilterParameterParent[]>();
	const [filterState, setFilterState] = React.useState<FilterState>({});

	const fields = useRecoilValue(AFormFields);
	const fieldMap: { [key: number | string]: Field } = React.useMemo(() => fields.reduce((acc, f) => ({ ...acc, [f.id]: f, [f.slug]: f }), {}), [fields]);
	const fieldColumns: { [key: number]: Field } = React.useMemo(() => template?.screens.flatMap(s => s.fields).reduce((acc, f) => {
		if (Object.keys(f.metadata).length !== 0) return acc;
		if (f.field_id !== undefined) acc[f.field_id] = fieldMap[f.field_id];
		else if (f.slug !== undefined) {
			const field = fieldMap[f.slug];
			if (field !== undefined) acc[field.id] = field;
		}
		return acc;
	}, {}) ?? {}, [template, fieldMap]);
	const [viewLoaded, setViewLoaded] = React.useState(false);
	const [isLeftModalOpen, setLeftModalOpen] = React.useState<boolean>(true);


	React.useEffect(() => {
		const f = ({ isOpen, isFullOpen, id }) => setInstanceModal({ isOpen, data: id, fullOpenMode: isFullOpen });
		checkUrlModalParams(f, 'instance')();
		history.listen(checkUrlModalParams(f, 'instance'));
	}, [history]);

	React.useEffect(() => {
		if (history.location.hash !== `#${view}` && isValidHash(history.location.hash.slice(1))) {
			setView(history.location.hash.slice(1));
		}
	}, [history.location]);

	const openPopup = React.useCallback((view: ViewEnum) => (uuid: string) => {
		history.replace({
			search: `?id=${formId}&instance=${uuid}`,
			hash: view,
		});
	}, [formId]);

	const onFormChange = React.useCallback((template: FormTemplate) => {
		history.replace({
			search: `?id=${template.id}`,
			hash: view,
		});
	}, [view]);

	React.useEffect(() => {
		const asyncCall = async() => {
			const instanceFilters: FilterParameterParent = { children: (await getFormInstancesFilters()).filters, name: translateToString('forms') };
			const companyFilterParameters = [...(await getCompanyFilters()).filters, ...(await generateFormFilters(additionalColumns, additionalFieldColumns, calculatedFields)).filters];
			const companyFilters: FilterParameterParent = {
				children: companyFilterParameters.map<FilterParameter>((category) => ({
					category: category.category,
					filters: category.filters.map((filter) => ({
						...filter,
						id: { company: filter.id, wrapper: 'company' },
					}))
				})), name: translateToString('companies')
			};
			setBaseFilterParams([instanceFilters, companyFilters]);
		};
		asyncCall();
	}, []);

	React.useEffect(() => {
		if (!baseFilterParams.length) return;
		const formFilters: FilterParameterParent = { ...baseFilterParams[0] };
		formFilters.children = [...formFilters.children as FilterParameter[], {
			category: translateToString('additional_field_columns'), filters: Object.values(fieldColumns).map<Filter>((fc): Filter => ({
				id: { additional_field_columns: [fc.id] },
				name: fc.name,
				type: fieldTypeToFilterType(fc.type)
			}))
		}];
		setFilterParams([formFilters, ...baseFilterParams.slice(1)]);
	}, [baseFilterParams, formId]);

	React.useEffect(() => {
		const localHiddenColumns = JSON.parse(localStorage.getItem(FORM_HIDDEN_LOCAL_STORAGE + `[${formId}]`) || '[]');
		const localSortColumns = JSON.parse(localStorage.getItem(FORM_SORT_LOCAL_STORAGE + `[${formId}]`) || '[]');
		if (localSortColumns) setSortColumns(localSortColumns);
		if (localHiddenColumns) setHiddenColumns(localHiddenColumns);
	}, [formId]);

	const filterView = React.useCallback((view: DbView<FormView>) => view.data.formId === formId, [formId]);

	React.useEffect(() => {
		props.setToolBarState({
			title: template?.name ?? translateToString('form_instance'),
			bottomLeftToolbarComponent: <ToolbarBox>
				<ModalLeftToggle isOpen={isLeftModalOpen} onClick={() => setLeftModalOpen(open => !open)} />
				<ToolBarView
					localStorageKey={FORM_VIEW_LOCAL_STORAGE + `[${formId}]`}
					localStorageHiddenKey={FORM_HIDDEN_LOCAL_STORAGE + `[${formId}]`}
					viewsKey='forms'
					context={context}
					dataFormatter={data => ({ ...data, formId })}
					viewsFilters={filterView}
					onLoad={() => setViewLoaded(true)}
				>
					<ToolbarFilterButton
						activeFilters={filterTreeLength(filterResult?.formatted)}
						onClick={() => setFilterState(state => ({ ...state, openSummary: true, filterOpen: true }))}
						onDeleteFilter={() => {
							setFilterResult({ values: { combinator: 'and', array: [] }, formatted: undefined });
						}}
					/>
					{subComponentToolBarState?.bottomLeftToolbarComponent}
				</ToolBarView>
			</ToolbarBox>,
			bottomRightToolbarComponent: <ToolbarBox>
				<IconsGroupBackground background={true}>
					<SwapImageContainer selected={view === 'list'} onClick={_ => setView('list')}><Menu /></SwapImageContainer>
					<SwapImageContainer selected={view === 'kanban'} onClick={_ => setView('kanban')}><LayoutGrid /></SwapImageContainer>
					<SwapImageContainer selected={view === 'gallery'} onClick={_ => setView('gallery')}><Image /></SwapImageContainer>
				</IconsGroupBackground>
				{subComponentToolBarState?.bottomRightToolbarComponent}
			</ToolbarBox>
		});
	}, [subComponentToolBarState, view, filterResult, filterResult, hiddenColumns, sortColumns, formId, isLeftModalOpen]);

	return <>
		<GalleryModalLeft
			templateList={forms}
			selected={template}
			setSelected={onFormChange}
			isModalOpen={isLeftModalOpen}
			setIsModalOpen={setLeftModalOpen}
		/>
		<GalleryRightPartWrapper isModalOpen={isLeftModalOpen} isGallery={view === 'gallery'}>
			{view === 'list' && <FormInstanceList {...props} openPopup={openPopup('list')} viewLoaded={viewLoaded} setFilterState={setFilterState} fieldColumns={fieldColumns} fieldMap={fieldMap} setToolBarState={setSubComponentToolBarState} template={template} formId={formId} />}
			{view === 'kanban' && <FormInstanceKanban {...props} openPopup={openPopup('kanban')} viewLoaded={viewLoaded} setFilterState={setFilterState} fieldColumns={fieldColumns} fieldMap={fieldMap} setToolBarState={setSubComponentToolBarState} template={template} formId={formId} />}
			{view === 'gallery' && <FormInstanceGallery {...props} openPopup={openPopup('gallery')} viewLoaded={viewLoaded} setFilterState={setFilterState} fieldColumns={fieldColumns} fieldMap={fieldMap} setToolBarState={setSubComponentToolBarState} template={template} formId={formId} />}
		</GalleryRightPartWrapper>
		<AdvancedFilters
			permission='ReadForm'
			isOpen={filterState.filterOpen ?? false}
			setOpen={filterOpen => setFilterState({ filterOpen })}
			isOpenSummary={filterState.openSummary ?? false}
			filterList={filterParams}
			filterValues={filterResult?.values}
			onChange={(value) => setFilterResult(value)}
			selectedFilter={filterState.selectedId}
		/>

		<InstancePopup
			isOpen={instanceModal.isOpen}
			onClickOut={() => {
				setInstanceModal({ ...instanceModal, isOpen: false, });
				history.push({ search: `?id=${formId}`, hash: view });
			}}
			modalState={instanceModal}
		/>
	</>;

}
